/**
 * Hook for fetching sparkline data from the dedicated lightweight endpoint.
 *
 * PERFORMANCE: Uses /sparkline endpoint which returns ~30 daily-aggregated
 * points per parameter, instead of the full dashboard endpoint which returns
 * thousands of raw readings.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';

/** Sparkline data point for the Sparkline component */
export interface SparklinePoint {
  value: number | null;
}

/** Shape returned by the /sparkline endpoint */
interface SparklineApiPoint {
  date: string;
  value: number;
  min_value?: number;
  max_value?: number;
}

export interface SparklineResponse {
  sensor: Record<string, SparklineApiPoint[]>;
  water_test: Record<string, SparklineApiPoint[]>;
}

/**
 * Extract sparkline data points for a given parameter key from the
 * pre-aggregated sparkline response.
 */
export function extractSparklinePoints(
  sparklineData: SparklineResponse | undefined,
  paramKey: string,
): SparklinePoint[] {
  if (!sparklineData) return [];

  // Check sensor data first, then water test data
  const points = sparklineData.sensor[paramKey] ?? sparklineData.water_test[paramKey];
  if (!points) return [];

  // Interleave min/max per day to create an "envelope" that shows daily range.
  // This produces 2 points per day (60 points for 30 days) — lightweight but
  // captures oscillation that single-point-per-day destroys.
  const result: SparklinePoint[] = [];
  for (const p of points) {
    if (p.min_value != null && p.max_value != null) {
      result.push({ value: p.min_value });
      result.push({ value: p.max_value });
    } else {
      // Backward compatibility: if no min/max, use the value directly
      result.push({ value: p.value });
    }
  }
  return result;
}

/**
 * Hook to fetch 30-day sparkline data for dashboard tiles.
 *
 * Uses the dedicated /sparkline endpoint which returns daily aggregates,
 * reducing payload size from ~50-100KB to ~2-3KB.
 */
export function useSparklineData() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery({
    queryKey: ['sparkline-data', tankId],
    queryFn: async () => {
      const response = await api.get<SparklineResponse>(
        `/api/tanks/${tankId}/sparkline`
      );
      return response;
    },
    enabled: !!tankId,
    staleTime: CACHE_TIMES.DATA, // sparklines don't need frequent updates
    retry: 1, // Sparkline is non-critical UI; don't retry aggressively
  });
}
